import React, { useEffect, useState } from "react";
import { db } from "../config/firebaseConfig";
import { ref, get, onValue } from "firebase/database";
import { isMobileScreen } from "../lib/utils";
import { IoClose, IoEyeSharp } from "react-icons/io5";
import SortupDialog from "./common/SortupDialog";

interface BannerDetail {
  title: string;
  content: string;
}

interface BannerDetailData {
  intro: string;
  bannerDetail: BannerDetail[];
}

const BannerDetail = () => {
  const [bannerData, setBannerData] = useState<BannerDetailData>({
    intro: "",
    bannerDetail: [],
  });
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(isMobileScreen);
  const [isVisible, setIsVisible] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 500); // or whatever breakpoint you're using
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const bannerDetailRef = ref(db, "LayoutSettingData/BannerDetail");

    // Set up real-time listener
    const unsubscribe = onValue(bannerDetailRef, (snapshot) => {
      try {
        if (snapshot.exists()) {
          const data = snapshot.val();
          if (data && data.bannerDetail && Array.isArray(data.bannerDetail)) {
            setBannerData(data);
          } else {
            console.error("Invalid banner data structure");
            setBannerData({ intro: "", bannerDetail: [] });
          }
        } else {
          setBannerData({ intro: "", bannerDetail: [] });
        }
      } catch (error) {
        console.error("Error fetching banner data:", error);
        setBannerData({ intro: "", bannerDetail: [] });
      } finally {
        setLoading(false);
      }
    });

    // Clean up listener when component unmounts
    return () => unsubscribe();
  }, []); // Empty dependency array since we want this to run once on mount

  useEffect(() => {
    if (bannerData?.bannerDetail?.length > 1) {
      const showNextBanner = () => {
        if (!isAnimating) {
          setIsAnimating(true);

          setTimeout(() => {
            setCurrentIndex((prev) =>
              prev === bannerData.bannerDetail.length - 1 ? 0 : prev + 1
            );
            setIsAnimating(false);
          }, 50000);
        }
      };

      const timer = setTimeout(showNextBanner, 50000);

      return () => clearTimeout(timer);
    }
  }, [bannerData.bannerDetail.length, currentIndex, isAnimating]);

  const handleEyeClick = () => {
    setIsDialogOpen(true);
  };

  if (loading || bannerData.bannerDetail.length === 0 || !isVisible) {
    return null;
  }

  return (
    <>
      <div
        className={`h-10  overflow-hidden bg-customDivColor rounded-3xl shadow-sm mx-2 ${
          isMobile ? "hidden" : "w-[40vw]"
        }`}
      >
        <div className="h-full flex items-center gap-2 px-2">
          {/* Banner Content */}
          <div className="flex-1 overflow-hidden">
            <p
              className={`text-gray-700 font-medium whitespace-nowrap animate-slide-left ${
                isAnimating ? "" : "animation-paused"
              }`}
              style={{
                textShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
              }}
            >
              <span className="font-bold mr-2 text-customGreenColor">
                {bannerData.bannerDetail[currentIndex].title}:
              </span>
              {bannerData.bannerDetail[currentIndex].content}
            </p>
          </div>

          {/* Add Close Button */}
          <div className="flex">
            <button
              onClick={handleEyeClick}
              className="p-2 rounded-full hover:bg-customGreenColor/10 text-gray-500 hover:text-customGreenColor transition-all duration-300"
              title="View All Announcements"
            >
              <IoEyeSharp size={20} />
            </button>
            <button
              onClick={() => setIsVisible(false)}
              className="p-2 rounded-full hover:bg-red-50 text-gray-500 hover:text-red-600 transition-all duration-300"
              title="Close Banner"
            >
              <IoClose size={20} />
            </button>
          </div>
        </div>
      </div>

      <SortupDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        header="Announcements & Updates"
        bodyClassName="w-full min-w-[37vw]"
      >
        <div className="space-y-6 p-4">
          {bannerData.bannerDetail.map((banner, index) => (
            <div
              key={index}
              className="border-b pb-6 last:border-b-0 hover:bg-gray-50 p-4 rounded-lg transition-colors duration-300"
            >
              <h3 className="font-bold text-lg mb-3 text-customGreenColor">
                {banner.title}
              </h3>
              <p className="text-gray-700 leading-relaxed">{banner.content}</p>
            </div>
          ))}
        </div>
      </SortupDialog>
    </>
  );
};

export default BannerDetail;
